import { Empty, Skeleton } from 'antd';
import axios from 'axios';
import classNames from 'classnames/bind';
import { CONTENT_COLLECTION, DEFAULT_LOCALE, Locale } from 'constants/locale';
import useAPI from 'hooks/useAPI';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import qs from 'qs';
import { useCallback, useState } from 'react';
import { IQuery } from 'types/common';
import { IJobItem } from 'types/recruit';
import { toLocale } from 'utils/locale';
import {
  filterJobs,
  getJobFilterOptions,
  JobFilterField,
  JobFilters,
} from 'utils/recruit';
import JobCard from '../JobCard';
import JobFilter from '../JobFilter';
import styles from './JobList.module.css';

const cx = classNames.bind(styles);

interface IGetJobs {
  locale?: Locale;
}

function JobList() {
  const { t } = useTranslation('recruit-jobs');
  const { locale } = useRouter();

  const getJobs = useCallback(async ({ locale = DEFAULT_LOCALE }: IGetJobs) => {
    const query: IQuery = {
      sort: 'displayOrder:asc',
      pagination: {
        limit: -1,
      },
    };
    const route = CONTENT_COLLECTION[locale].job;
    return axios.get(`/api/${route}/?${qs.stringify(query)}`);
  }, []);

  const { data, isLoading } = useAPI<IJobItem[]>((args) =>
    getJobs({ locale: toLocale(locale), ...args })
  );

  return (
    <section className={cx('openings')}>
      <h2 className={cx('title')}>{t('openings-title')}</h2>
      <JobListBody
        key={locale}
        jobs={data?.data ?? null}
        isLoading={isLoading}
      />
    </section>
  );
}

interface IJobListBodyProps {
  jobs: IJobItem[] | null;
  isLoading: boolean;
}

function JobListBody({ jobs, isLoading }: IJobListBodyProps) {
  const { t } = useTranslation('recruit-jobs');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<JobFilters>({});

  if (isLoading) return <Skeleton active />;

  if (!jobs?.length) return <Empty description={t('empty')} />;

  const options = getJobFilterOptions({ jobs });
  const filteredJobs = filterJobs({ jobs, search, filters });

  const handleFilterChange = (field: JobFilterField, value: string) =>
    setFilters((prev) => ({ ...prev, [field]: value || undefined }));

  return (
    <>
      <JobFilter
        search={search}
        filters={filters}
        options={options}
        onSearchChange={setSearch}
        onFilterChange={handleFilterChange}
      />
      {filteredJobs.length ? (
        <ul className={cx('list')}>
          {filteredJobs.map((job) => (
            <JobCard key={job.id} job={job} />
          ))}
        </ul>
      ) : (
        <Empty description={t('empty')} />
      )}
    </>
  );
}

export default JobList;
